import { FILTERS_CODE_SEPARATOR } from 'core/constants/platform.constants';
import type {
  IBranch,
  ICustomAutocompleteOption,
  IFiltersByCodeFormValues,
  IGroupedAutocompleteOption,
  IPriceGroup,
  IPriceList,
  IZone,
  TSelectedDistributorCode,
  TSelectedPriceListCode,
} from 'core/model/interfaces/master-entities';
import { IBranchFilterDTO } from 'core/services/master-entities/master-entities.dto';

/**
 * It maps a distributorId in the format "zoneId-branchId-distributorId" to a string
 */
const mapDistributorId = (zone: IZone, branch: IBranch): TSelectedDistributorCode =>
  `${zone.zoneId}${FILTERS_CODE_SEPARATOR}${branch.branchId}${FILTERS_CODE_SEPARATOR}${branch.distributor.distributorId}`;

const mapPriceListId = (priceGroup: IPriceGroup, priceList: IPriceList): TSelectedPriceListCode =>
  `${priceGroup.priceGroupCode}${FILTERS_CODE_SEPARATOR}${priceList.priceListId}`;

export const mapBranchOption = (zone: IZone) => (branch: IBranch) => {
  const value = mapDistributorId(zone, branch);

  const distributor: ICustomAutocompleteOption = {
    value,
    /**
     * @description Display the branch instead of the distributor
     */
    label: `${branch.branchId}-${branch.branchName}`,
  };

  return distributor;
};

export const mapZoneOption = (zone: IZone): IGroupedAutocompleteOption => {
  const branchListOptionList = zone.branches.map(mapBranchOption(zone));

  return {
    value: zone.zoneId,
    label: `${zone.zoneId}-${zone.zoneName}`,
    options: branchListOptionList,
  };
};

/**
 * Maps the zone, branch, and distributor data into a format suitable for a combined list.
 * The format is {@link mapDistributorId | "zoneId-branchId-distributorId"}.
 */
export const toCombineDistributorIdListByZoneList = (zoneList: Array<IZone>) => {
  /**
   * We use flatMap to iterate over each zone and then map over its branches,
   * flattening the resulting array of arrays into a single array of strings.
   * This allows us to create a list of distributor IDs that combines the zone and branch information.
   * The result is an array of strings in the format {@link mapDistributorId | "zoneId-branchId-distributorId"}.
   */
  const idList = zoneList.flatMap(zone => zone.branches.map(branch => mapDistributorId(zone, branch)));

  return idList;
};

/**
 * Processes the 'priceGroups' array from the root object to generate a list of combined IDs.
 * The format for each ID is {@link mapPriceListId | "priceGroupCode-priceListId"}.
 */
export const toCombinePriceListByPriceGroupList = (priceGroupList: Array<IPriceGroup>) => {
  const idList = priceGroupList.flatMap(group => group.priceLists.map(list => mapPriceListId(group, list)));

  return idList;
};

export const mapPriceListOption =
  (priceGroup: IPriceGroup) =>
  (priceList: IPriceList): ICustomAutocompleteOption => {
    const value = mapPriceListId(priceGroup, priceList);

    return {
      value,
      label: `${priceList.priceListCode}-${priceList.priceListName}`,
    };
  };

export const mapPriceGroupOption = (priceGroup: IPriceGroup): IGroupedAutocompleteOption => {
  const priceListOptionList = priceGroup.priceLists.map(mapPriceListOption(priceGroup));

  return {
    value: priceGroup.priceGroupCode,
    label: `${priceGroup.priceGroupCode}-${priceGroup.priceGroupName}`,
    options: priceListOptionList,
  };
};

/**
 * Maps the distributorIdList for the select component based on the selected zones
 * and the previous selected zones.
 *
 * - If a zone is added, it adds the distributors from that zone to the list.
 * - If a zone is removed, it filters out the distributors from that zone.
 */
export const mapDistributorIdListForSelect = (
  formValues: IFiltersByCodeFormValues,
  selectedZoneOptionList: Array<IZone>,
  previousSelectedZoneList: Array<IZone>,
) => {
  /**
   * By default, the select does not allow to select multiple price groups at the same time.
   * However, if the user selects all zones by clicking on the "TODOS" option,
   * the select will allow to select multiple zones.
   *
   * This is a workaround to handle that case.
   */
  const hasSelectedAllAtTheSameTime = selectedZoneOptionList.length - previousSelectedZoneList.length > 1;

  if (hasSelectedAllAtTheSameTime) {
    const newDistributorIdList = selectedZoneOptionList.flatMap(zone =>
      zone.branches.map(branch => mapDistributorId(zone, branch)),
    );

    return newDistributorIdList;
  }

  const zoneAdded = selectedZoneOptionList.find(
    zone => !previousSelectedZoneList.some(previousZone => previousZone.zoneId === zone.zoneId),
  );

  if (zoneAdded) {
    const newDistributorIdList = zoneAdded.branches.map(branch => mapDistributorId(zoneAdded, branch));

    return [...formValues.distributorIdList, ...newDistributorIdList];
  }

  const zoneRemoved = previousSelectedZoneList.find(
    previousZone => !selectedZoneOptionList.some(zone => zone.zoneId === previousZone.zoneId),
  );

  if (zoneRemoved) {
    /**
     * If a zone is removed, we need to filter the distributorIdList
     * to remove the distributors that belong to that zone
     */
    const filteredDistributorIdList = formValues.distributorIdList.filter(distributorId => {
      const [zoneIdFromDistributor] = distributorId.split(FILTERS_CODE_SEPARATOR);

      return zoneIdFromDistributor !== zoneRemoved.zoneId;
    });

    return filteredDistributorIdList;
  }

  return [];
};

/**
 * Maps the priceListIdList for the select component based on the selected price groups
 * and the previous selected price groups.
 *
 * - If a price group is added, it adds the price lists from that price group to the list.
 * - If a price group is removed, it filters out the price lists from that price group.
 */
export const mapPriceListIdListForSelect = (
  formValues: IFiltersByCodeFormValues,
  selectedPriceGroupOptionList: Array<IPriceGroup>,
  previousSelectedPriceGroupList: Array<IPriceGroup>,
) => {
  /**
   * By default, the select does not allow to select multiple price groups at the same time.
   * However, if the user selects all price groups by clicking on the "TODOS" option,
   * the select will allow to select multiple price groups.
   *
   * This is a workaround to handle that case.
   */
  const hasSelectedAllAtTheSameTime = selectedPriceGroupOptionList.length - previousSelectedPriceGroupList.length > 1;

  if (hasSelectedAllAtTheSameTime) {
    const newPriceListIdList = selectedPriceGroupOptionList.flatMap(priceGroup =>
      priceGroup.priceLists.map(priceList => mapPriceListId(priceGroup, priceList)),
    );

    return newPriceListIdList;
  }

  const priceGroupAdded = selectedPriceGroupOptionList.find(
    priceGroup =>
      !previousSelectedPriceGroupList.some(
        previousPriceGroup => previousPriceGroup.priceGroupCode === priceGroup.priceGroupCode,
      ),
  );

  if (priceGroupAdded) {
    const priceListIdList = priceGroupAdded.priceLists.map(priceList => mapPriceListId(priceGroupAdded, priceList));

    return [...formValues.priceListIdList, ...priceListIdList];
  }

  const priceGroupRemoved = previousSelectedPriceGroupList.find(
    previousPriceGroup =>
      !selectedPriceGroupOptionList.some(priceGroup => priceGroup.priceGroupCode === previousPriceGroup.priceGroupCode),
  );

  if (priceGroupRemoved) {
    /**
     * If a price group is removed, we need to filter the priceListIdList
     * to remove the price lists that belong to that price group
     */
    const filteredPriceListIdList = formValues.priceListIdList.filter(priceListId => {
      const [priceGroupCodeFromPriceList] = priceListId.split(FILTERS_CODE_SEPARATOR);

      return priceGroupCodeFromPriceList !== priceGroupRemoved.priceGroupCode;
    });

    return filteredPriceListIdList;
  }

  return [];
};

/**
 * It returns a list of zone IDs that match the provided branch filter codes.
 * The branch filter codes are in the format "zoneId-branchId-distributorId".
 * It filters the zones based on the branch filter codes and returns the unique zone IDs.
 */
export const mapMatchZoneIdListByBranchFilterCodeList = (
  zones: Array<IZone>,
  branchFilterCodeList: IFiltersByCodeFormValues['distributorIdList'],
) => {
  const zoneIdList = branchFilterCodeList.map(branchCode => {
    const [zoneId] = branchCode.split(FILTERS_CODE_SEPARATOR);

    return zoneId;
  });

  const storedZoneIdList = zones.map(zone => zone.zoneId);

  const uniqueZoneIdList = Array.from(new Set(storedZoneIdList));

  const filteredZoneIdList = uniqueZoneIdList.filter(zoneId => zoneIdList.includes(zoneId));

  return filteredZoneIdList;
};

export const toMapBranchFilterByDistributorFilterCode = (
  distributorIdList: IFiltersByCodeFormValues['distributorIdList'],
) => {
  const branches = distributorIdList.map(value => {
    const [, branchId, distributorId] = value.split(FILTERS_CODE_SEPARATOR);

    const branch: IBranchFilterDTO = {
      id: branchId,
      distributorId,
    };

    return branch;
  });

  return branches;
};

/**
 * It returns a list of {@link IPriceGroup.priceGroupCode | price group id} based on the provided `price list filter codes`.
 * The price list filter codes are in the format `"priceGroupCode-priceListId"`.
 * It maps the price list filter codes to the price group codes.
 */
export const toMapPriceGroupFilterByPriceListFilterCode = (
  priceListIdList: IFiltersByCodeFormValues['priceListIdList'],
) => {
  const priceGroups = priceListIdList.map(value => {
    const [priceGroupCode] = value.split(FILTERS_CODE_SEPARATOR);

    return priceGroupCode as `${IPriceGroup['priceGroupCode']}`;
  });

  return priceGroups;
};

/**
 * It returns a list of {@link IPriceList.priceListId | price list id} based on the provided `price list filter codes`.
 * The price list filter codes are in the format `"priceGroupCode-priceListId"`.
 */
export const toMapPriceListFilterByPriceListFilterCode = (
  priceListIdList: IFiltersByCodeFormValues['priceListIdList'],
) => {
  const priceListIds = priceListIdList.map(value => {
    const [, priceListId] = value.split(FILTERS_CODE_SEPARATOR);

    return priceListId as `${IPriceList['priceListId']}`;
  });

  return priceListIds;
};

/**
 * It filters the zone list based on the `distributor filter codes`.
 * To do this, it extracts the `zone IDs` from the `distributor` filter codes by splitting them
 * at the {@link FILTERS_CODE_SEPARATOR} and returns the `filtered zone list`.
 */
export const filterZoneListFromDistributorFilterCode = (
  zones: Array<IZone>,
  distributorFilterCodeList: IFiltersByCodeFormValues['distributorIdList'],
) => {
  const zoneIdList = distributorFilterCodeList.map(distributorCode => {
    const [zoneId] = distributorCode.split(FILTERS_CODE_SEPARATOR);

    return zoneId;
  });

  const filteredZoneOptionList = zones.filter(zone => zoneIdList.some(zoneId => zoneId === zone.zoneId));

  return filteredZoneOptionList;
};

export const filterPriceGroupListFromPriceListFilterCode = (
  priceGroupList: Array<IPriceGroup>,
  priceListFilterCodeList: IFiltersByCodeFormValues['priceListIdList'],
) => {
  const priceGroupCodeList = toMapPriceGroupFilterByPriceListFilterCode(priceListFilterCodeList);

  const filteredPriceGroupOptionList = priceGroupList.filter(priceGroup =>
    priceGroupCodeList.some(priceGroupCode => priceGroupCode === priceGroup.priceGroupCode),
  );

  return filteredPriceGroupOptionList;
};
